import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { FileSpreadsheet, FileText, BarChart3, Users, ShoppingCart, Package, TrendingUp, Archive, ArchiveRestore, ChevronDown, ChevronUp, Trophy, Truck } from 'lucide-react';
import api from '../lib/api';
import ConsolidatedReportDialog from './ConsolidatedReportDialog';
import EventComparisonDialog from './EventComparisonDialog';
import PartywiseReportDialog from './PartywiseReportDialog';

/**
 * Event Dashboard — one tile per event with quick stats and a one-click
 * drill-in to the consolidated report. Acts like a campaign scoreboard.
 *
 * Archived events live in a collapsible "Past Events" section so the
 * scoreboard always foregrounds live campaigns.
 */
const EventDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [reportEventId, setReportEventId] = useState('');
  const [reportEventName, setReportEventName] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [downloadingFmt, setDownloadingFmt] = useState(null);
  const [busyEventId, setBusyEventId] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [partywiseEventId, setPartywiseEventId] = useState('');
  const [partywiseEventName, setPartywiseEventName] = useState('');
  const [partywiseOpen, setPartywiseOpen] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState(new Set());

  const toggleEventSelect = (eid) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid); else next.add(eid);
      return next;
    });
  };

  const openCombinedReport = () => {
    if (selectedEventIds.size === 0) return;
    setReportEventId('');
    setReportEventName([...selectedEventIds].map(eid => events.find(e => e.id === eid)?.name).filter(Boolean).join(' + '));
    setReportOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/events/dashboard?include_archived=true');
      setEvents(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load event dashboard');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openReport = (ev) => {
    setReportEventId(ev.id);
    setReportEventName(ev.name);
    setReportOpen(true);
  };

  const openPartywise = (ev) => {
    setPartywiseEventId(ev.id);
    setPartywiseEventName(ev.name);
    setPartywiseOpen(true);
  };

  const download = async (ev, fmt) => {
    setDownloadingFmt(`${ev.id}-${fmt}`);
    try {
      const res = await api.post(
        `/sales-orders/consolidated-report/export?fmt=${fmt}`,
        { event_id: ev.id },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `so-consolidated-report-${ev.name.replace(/\s+/g, '_')}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      let detail = 'Failed to download';
      if (err.response?.data?.text) {
        try { const t = await err.response.data.text(); detail = JSON.parse(t).detail || detail; } catch { /* noop */ }
      }
      toast.error(detail);
    } finally { setDownloadingFmt(null); }
  };

  const toggleArchive = async (ev) => {
    setBusyEventId(ev.id);
    try {
      const path = ev.is_archived ? `/events/${ev.id}/unarchive` : `/events/${ev.id}/archive`;
      await api.post(path);
      toast.success(ev.is_archived ? `"${ev.name}" restored` : `"${ev.name}" archived`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally { setBusyEventId(null); }
  };

  const activeEvents = events.filter(e => !e.is_archived);
  const archivedEvents = events.filter(e => e.is_archived);

  const totals = activeEvents.reduce((acc, e) => ({
    orders: acc.orders + e.order_count,
    qty: acc.qty + e.total_qty,
    amount: acc.amount + e.total_amount,
  }), { orders: 0, qty: 0, amount: 0 });

  return (
    <div className="p-6 space-y-6" data-testid="event-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-pink-600" />
            Event Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Live scoreboard for every sales campaign / exhibition.</p>
        </div>
        <Button
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          disabled={events.length < 2}
          onClick={() => setCompareOpen(true)}
          data-testid="open-event-compare-btn"
        >
          <Trophy className="w-4 h-4 mr-1.5" />
          Compare Events
        </Button>
      </div>

      {/* Aggregate strip */}
      {!loading && events.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="event-dashboard-totals">
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Events</div>
            <div className="text-3xl font-bold text-pink-700 mt-1">{events.length}</div>
          </div>
          <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Orders</div>
            <div className="text-3xl font-bold text-sky-700 mt-1">{totals.orders.toLocaleString('en-IN')}</div>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Qty</div>
            <div className="text-3xl font-bold text-emerald-700 mt-1">{totals.qty.toLocaleString('en-IN')}</div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Value</div>
            <div className="text-3xl font-bold text-amber-700 mt-1">₹{totals.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-gray-400 text-center py-16">Loading…</p>}

      {!loading && events.length === 0 && (
        <Card className="p-12 text-center border-dashed">
          <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700">No events yet</h3>
          <p className="text-sm text-gray-500 mt-1">Create an event from the Sales Order form to start tracking campaigns here.</p>
        </Card>
      )}

      {!loading && activeEvents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="event-tiles">
          {activeEvents.map(ev => renderTile(ev))}
        </div>
      )}

      {!loading && archivedEvents.length > 0 && (
        <div className="pt-2" data-testid="archived-events-section">
          <button
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 py-2"
            onClick={() => setShowArchived(s => !s)}
            data-testid="toggle-archived-events"
          >
            {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Past Events <span className="text-xs text-gray-400">({archivedEvents.length})</span>
          </button>
          {showArchived && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3 opacity-90" data-testid="archived-event-tiles">
              {archivedEvents.map(ev => renderTile(ev))}
            </div>
          )}
        </div>
      )}

      {/* Multi-event selection bar */}
      {selectedEventIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-4 animate-fadeIn" data-testid="event-multi-select-bar">
          <span className="text-sm font-medium">{selectedEventIds.size} event{selectedEventIds.size > 1 ? 's' : ''} selected</span>
          <Button size="sm" variant="ghost" className="text-pink-300 hover:text-pink-200 hover:bg-pink-900/30 gap-1.5"
            onClick={openCombinedReport} data-testid="event-combined-report-btn">
            <BarChart3 className="w-4 h-4" /> Combined Report
          </Button>
          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white"
            onClick={() => setSelectedEventIds(new Set())} data-testid="event-clear-selection">
            ✕
          </Button>
        </div>
      )}

      <ConsolidatedReportDialog
        open={reportOpen}
        onOpenChange={(o) => { setReportOpen(o); if (!o) { setReportEventId(''); setReportEventName(''); setSelectedEventIds(new Set()); } }}
        eventId={reportEventId || null}
        eventIds={selectedEventIds.size > 0 ? [...selectedEventIds] : null}
        eventName={reportEventName || null}
      />
      <PartywiseReportDialog
        open={partywiseOpen}
        onOpenChange={(o) => { setPartywiseOpen(o); if (!o) { setPartywiseEventId(''); setPartywiseEventName(''); } }}
        eventId={partywiseEventId || null}
        eventName={partywiseEventName || null}
      />
      <EventComparisonDialog open={compareOpen} onOpenChange={setCompareOpen} />
    </div>
  );

  function renderTile(ev) {
    const isEmpty = ev.order_count === 0;
    const archived = ev.is_archived;
    const accent = archived ? 'border-l-gray-400' : (isEmpty ? 'border-l-gray-300' : 'border-l-pink-500');
    return (
      <Card key={ev.id} className={`p-5 border-l-4 ${accent} ${archived ? 'bg-gray-50/60' : ''} ${selectedEventIds.has(ev.id) ? 'ring-2 ring-pink-400' : ''} hover:shadow-md transition-shadow`} data-testid={`event-tile-${ev.id}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {!isEmpty && (
                <input type="checkbox" className="w-4 h-4 accent-pink-600 flex-shrink-0"
                  checked={selectedEventIds.has(ev.id)}
                  onChange={() => toggleEventSelect(ev.id)}
                  onClick={e => e.stopPropagation()}
                  data-testid={`event-select-${ev.id}`} />
              )}
              <h3 className="text-lg font-semibold text-gray-900 truncate" title={ev.name}>{ev.name}</h3>
              {/* Fulfilment status dot */}
              {!isEmpty && (
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ev.dispatch_pct >= 80 ? 'bg-emerald-500' : ev.dispatch_pct >= 30 ? 'bg-amber-400' : 'bg-red-400'}`}
                  title={`${ev.dispatch_pct}% dispatched`} />
              )}
            </div>
            {ev.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{ev.description}</p>}
          </div>
          <div className="flex gap-1 flex-shrink-0 ml-2">
            {archived && <Badge variant="outline" className="text-xs text-gray-500 border-gray-400 bg-white" data-testid={`event-archived-badge-${ev.id}`}>Archived</Badge>}
            {!archived && isEmpty && <Badge variant="outline" className="text-xs text-gray-400 border-gray-300">No SOs</Badge>}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 text-center mb-4">
          <div className="bg-sky-50 rounded p-2">
            <ShoppingCart className="w-4 h-4 text-sky-600 mx-auto mb-1" />
            <div className="text-lg font-bold text-sky-700">{ev.order_count}</div>
            <div className="text-[10px] text-gray-500 uppercase">Orders</div>
          </div>
          <div className="bg-emerald-50 rounded p-2">
            <Package className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
            <div className="text-lg font-bold text-emerald-700">{ev.total_qty}</div>
            <div className="text-[10px] text-gray-500 uppercase">Qty</div>
          </div>
          <div className="bg-amber-50 rounded p-2">
            <Users className="w-4 h-4 text-amber-600 mx-auto mb-1" />
            <div className="text-lg font-bold text-amber-700">{ev.customer_count}</div>
            <div className="text-[10px] text-gray-500 uppercase">Custs</div>
          </div>
        </div>

        <div className="text-sm font-semibold text-gray-900 mb-2 flex items-center justify-between">
          <span>Value</span>
          <span className="text-emerald-700">₹{ev.total_amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
        </div>

        {/* Revenue Earned */}
        {ev.revenue_earned > 0 && (
          <div className="text-sm text-gray-600 mb-2 flex items-center justify-between">
            <span>Revenue Earned</span>
            <span className="font-semibold text-blue-700">₹{ev.revenue_earned.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
          </div>
        )}

        {/* Dispatched Progress Bar */}
        {!isEmpty && (
          <div className="mb-3" data-testid={`event-dispatch-bar-${ev.id}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Dispatched</span>
              <span className={`font-bold ${ev.dispatch_pct >= 80 ? 'text-emerald-600' : ev.dispatch_pct >= 30 ? 'text-amber-600' : 'text-red-500'}`}>
                {ev.dispatched_qty || 0} / {ev.total_qty} pcs ({ev.dispatch_pct || 0}%)
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${ev.dispatch_pct >= 80 ? 'bg-emerald-500' : ev.dispatch_pct >= 30 ? 'bg-amber-400' : 'bg-red-400'}`}
                style={{ width: `${Math.min(100, ev.dispatch_pct || 0)}%` }} />
            </div>
          </div>
        )}

        {/* Top 3 designs */}
        {ev.top_designs.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Top Designs
            </div>
            <div className="space-y-1">
              {ev.top_designs.map((d, i) => (
                <div key={d.design_code} className="flex justify-between text-xs">
                  <span className="text-gray-700">
                    <span className="text-gray-400 mr-1">{i + 1}.</span>
                    <span className="font-medium">{d.design_code}</span>
                  </span>
                  <span className="font-semibold text-gray-900">{d.qty} pcs</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top 3 customers */}
        {ev.top_customers.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-1.5">Top Customers</div>
            <div className="space-y-1">
              {ev.top_customers.map((c, i) => (
                <div key={c.customer_id} className="flex justify-between text-xs">
                  <span className="text-gray-700 truncate">
                    <span className="text-gray-400 mr-1">{i + 1}.</span>{c.customer_name}
                  </span>
                  <span className="font-semibold text-gray-900 ml-2">{c.qty}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 pt-3 border-t space-y-2">
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-pink-600 hover:bg-pink-700 text-white"
              disabled={isEmpty}
              onClick={() => openReport(ev)}
              data-testid={`event-view-report-${ev.id}`}
            >
              View Report
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              disabled={isEmpty}
              onClick={() => openPartywise(ev)}
              data-testid={`event-partywise-btn-${ev.id}`}
            >
              <Users className="w-3.5 h-3.5 mr-1" />
              Partywise
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
              disabled={isEmpty}
              onClick={() => navigate('/ready-to-dispatch')}
              title="Ready to Dispatch"
              data-testid={`event-rtd-btn-${ev.id}`}
            >
              <Truck className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2"
            disabled={isEmpty || downloadingFmt === `${ev.id}-xlsx`}
            onClick={() => download(ev, 'xlsx')}
            title="Download Excel"
            data-testid={`event-dl-xlsx-${ev.id}`}
          >
            <FileSpreadsheet className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50 px-2"
            disabled={isEmpty || downloadingFmt === `${ev.id}-pdf`}
            onClick={() => download(ev, 'pdf')}
            title="Download PDF"
            data-testid={`event-dl-pdf-${ev.id}`}
          >
            <FileText className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="outline"
            className={`px-2 ${archived ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            disabled={busyEventId === ev.id}
            onClick={() => toggleArchive(ev)}
            title={archived ? 'Restore event' : 'Archive event'}
            data-testid={`event-archive-toggle-${ev.id}`}
          >
            {archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </Button>
          </div>
        </div>
      </Card>
    );
  }
};

export default EventDashboard;
